import { useEffect, useCallback } from 'react';
import { useAppDispatch } from 'state/hooks';
import { addNetworkRequest, updateNetworkRequest } from 'state/slices/network-slice';

export const useNetworkCapture = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const originalFetch = window.fetch;
    const originalXMLHttpRequest = window.XMLHttpRequest;

    // Capture fetch requests
    window.fetch = async (...args) => {
      const url = args[0] instanceof Request ? args[0].url : args[0].toString();
      const method = args[1]?.method || 'GET';
      const startTime = Date.now();
      
      // Capture request headers
      const requestHeaders: Record<string, string> = {};
      if (args[0] instanceof Request) {
        args[0].headers.forEach((value, key) => {
          requestHeaders[key] = value;
        });
      } else if (args[1]?.headers) {
        if (args[1].headers instanceof Headers) {
          args[1].headers.forEach((value, key) => {
            requestHeaders[key] = value;
          });
        } else {
          // Handle plain object headers
          Object.entries(args[1].headers).forEach(([key, value]) => {
            requestHeaders[key] = String(value);
          });
        }
      }
      
      const requestId = `fetch-${Date.now()}-${Math.random()}`;
      
      dispatch(addNetworkRequest({
        id: requestId,
        url,
        method: method.toUpperCase(),
        requestHeaders,
      }));

      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;
        
        // Clone response to read body without consuming it
        const responseClone = response.clone();
        let responseBody = '';
        
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json') || contentType.includes('text/')) {
            responseBody = await responseClone.text();
            // Limit response body size for performance
            if (responseBody.length > 10000) {
              responseBody = responseBody.substring(0, 10000) + '... (truncated)';
            }
          } else {
            responseBody = `[${contentType || 'binary data'}]`;
          }
        } catch (bodyError) {
          responseBody = '[Could not read response body]';
        }
        
        dispatch(updateNetworkRequest({
          id: requestId,
          updates: {
            status: response.status,
            duration,
            responseBody,
          }
        }));

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        dispatch(updateNetworkRequest({
          id: requestId,
          updates: {
            duration,
            error: error instanceof Error ? error.message : 'Network error',
          }
        }));

        throw error;
      }
    };

    // Capture XMLHttpRequest
    const OriginalXHR = originalXMLHttpRequest;
    window.XMLHttpRequest = function() {
      const xhr = new OriginalXHR();
      let requestId: string;
      let startTime: number;
      let url: string;
      let method: string;
      let requestHeaders: Record<string, string> = {};

      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      const originalSetRequestHeader = xhr.setRequestHeader;

      xhr.open = function(methodArg: string, urlArg: string | URL, async?: boolean, username?: string | null, password?: string | null) {
        method = methodArg.toUpperCase();
        url = urlArg.toString();
        requestId = `xhr-${Date.now()}-${Math.random()}`;
        requestHeaders = {}; // Reset headers for new request
        
        // @ts-ignore - Complex XMLHttpRequest typing
        return originalOpen.call(this, methodArg, urlArg, async, username, password);
      };

      xhr.setRequestHeader = function(name: string, value: string) {
        requestHeaders[name] = value;
        return originalSetRequestHeader.call(this, name, value);
      };

      xhr.send = function(body?: Document | XMLHttpRequestBodyInit | null) {
        startTime = Date.now();
        
        dispatch(addNetworkRequest({
          id: requestId,
          url,
          method,
          requestHeaders,
        }));

        return originalSend.call(this, body);
      };

      xhr.addEventListener('loadend', () => {
        const duration = Date.now() - startTime;
        
        let responseBody = '';
        try {
          if (xhr.responseText && xhr.responseText.length > 0) {
            responseBody = xhr.responseText;
            if (responseBody.length > 10000) {
              responseBody = responseBody.substring(0, 10000) + '... (truncated)';
            }
          }
        } catch (e) {
          responseBody = '[Could not read response body]';
        }
        
        dispatch(updateNetworkRequest({
          id: requestId,
          updates: {
            status: xhr.status,
            duration,
            error: xhr.status === 0 ? 'Network error' : undefined,
            responseBody,
          }
        }));
      });

      return xhr;
    } as any;

    // Restore originals on cleanup
    return () => {
      window.fetch = originalFetch;
      window.XMLHttpRequest = originalXMLHttpRequest;
    };
  }, [dispatch]);
}; 